import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  fetchCheckinContext,
  submitCheckin,
  type CheckinContext,
} from '../services/checkinPublicService';

type FormState = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes: string;
  consentMarketing: boolean;
  consentContact: boolean;
};

const defaultForm: FormState = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  notes: '',
  consentMarketing: false,
  consentContact: true,
};

const formatAddress = (addr?: CheckinContext['property'] extends { address: infer A } ? A : any) => {
  if (!addr) return '';
  if (addr.formattedAddress) return addr.formattedAddress;
  const parts = [addr.street, addr.city, addr.state, addr.postalCode].filter(Boolean);
  return parts.join(', ');
};

const SelfCheckin = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const agencySlug = searchParams.get('agency') || '';

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<CheckinContext | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [success, setSuccess] = useState<{ already?: boolean } | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('Missing check-in token');
        setLoading(false);
        return;
      }
      try {
        const data = await fetchCheckinContext(token);
        setContext(data);
      } catch (err: any) {
        const msg = err?.response?.data?.error || 'Invalid or expired check-in link';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'consentMarketing' || field === 'consentContact'
      ? (e.target as HTMLInputElement).checked
      : e.target.value;
    setForm(prev => ({ ...prev, [field]: value as any }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!context) return;
    setSubmitLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await submitCheckin(token, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        notes: form.notes.trim() || undefined,
        consentMarketing: form.consentMarketing,
        consentContact: form.consentContact,
      });
      setSuccess({ already: resp.alreadyCheckedIn });
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to submit check-in';
      setError(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const title = useMemo(() => {
    const agencyName = context?.agency?.name;
    const address = formatAddress(context?.property?.address);
    if (agencyName && address) return `${agencyName} • ${address}`;
    if (agencyName) return agencyName;
    if (address) return address;
    return 'Property Check-in';
  }, [context]);

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading check-in…</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !context) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{error || 'Invalid check-in link'}</Alert>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {success.already ? 'Already checked in' : 'Check-in successful'}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Thank you for checking in{context.property?.address?.formattedAddress ? ` at ${formatAddress(context.property.address)}` : ''}.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 6, mb: 6 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Visit date: {context.visitDateKey} | Expires: {new Date(context.expiresAt).toLocaleString()}
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="First name"
              margin="normal"
              required
              value={form.firstName}
              onChange={handleChange('firstName')}
            />
            <TextField
              fullWidth
              label="Last name"
              margin="normal"
              required
              value={form.lastName}
              onChange={handleChange('lastName')}
            />
            <TextField
              fullWidth
              label="Phone"
              margin="normal"
              required
              value={form.phone}
              onChange={handleChange('phone')}
            />
            <TextField
              fullWidth
              label="Email (optional)"
              margin="normal"
              value={form.email}
              onChange={handleChange('email')}
            />
            <TextField
              fullWidth
              label="Notes (optional)"
              margin="normal"
              multiline
              minRows={3}
              value={form.notes}
              onChange={handleChange('notes')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.consentContact}
                  onChange={handleChange('consentContact')}
                />
              }
              label="I consent to be contacted about this property"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.consentMarketing}
                  onChange={handleChange('consentMarketing')}
                />
              }
              label="I agree to receive marketing updates"
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              disabled={submitLoading}
            >
              {submitLoading ? 'Submitting...' : 'Check in'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default SelfCheckin;
